import { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  ListItem,
  Text,
  UnorderedList,
  useToast,
} from "@chakra-ui/react";

import PageHeader from "components/layout/PageHeader";

import StepTitle from "components/onboarding/StepTitle";
import StepDescription from "components/onboarding/StepDescription";
import DocumentsUpload from "components/elements/DocumentsUpload";
import { apiRequest, uploadDocument } from "utils/api";
import { supabase } from "index";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useNavigate } from "react-router-dom";
import { useFeatures } from "hooks/useFeatures";
import { useCurrentUserQuery } from "modules/tenant/queries/use-current-user-query";

const FinancialDocuments = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const { user } = useTenant();
  const { refetch } = useCurrentUserQuery();
  const [isLoading, setIsLoading] = useState(false);

  const [files, setFiles] = useState<any[]>([]);

  const { hasFeature } = useFeatures();
  const hasBankAccounts = hasFeature("connect_bank_accounts");

  const uploadDocuments = async () => {
    try {
      setIsLoading(true);

      // Require at least one document
      if (files.length === 0) {
        toast({
          title: "Documents Required",
          description: "Please upload at least one document to continue.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      // Upload documents
      for (const file of files) {
        await uploadDocument({ file });
      }
      toast({
        title: "Documents Uploaded",
        description: `${files.length} document(s) uploaded successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Mark user as onboarded
      await supabase.auth.updateUser({
        data: { onboarded: true },
      });

      // Log onboarding event - only if we have a company ID
      if (user?.lender_company?.id) {
        await apiRequest("/supabase/events/insert", {
          name: user.lender_company.slug
            ? `onboarded_${user.lender_company.slug}`
            : "onboarded",
          company: user.lender_company.id,
        });
      }

      await refetch();

      toast({
        title: "Onboarding Complete!",
        description: `${files.length} document(s) uploaded successfully. Welcome to your dashboard!`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Navigate to dashboard after successful onboarding
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to complete onboarding. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  function handleBack() {
    if (hasBankAccounts) {
      navigate("/setup/bank");
    } else {
      navigate("/setup/invite");
    }
  }

  return (
    <Box py="34px" px="26px" flex="1">
      <PageHeader title="Upload Documents" hideUser />
      <Divider mt="40px" mb="60px" />
      <StepTitle>Upload Documents</StepTitle>
      <StepDescription>Upload important documents associated with your company. At least one document is required to complete onboarding.</StepDescription>
      <Box maxW="500px">
        <HStack spacing="24px" mb="24px" alignItems="top" justifyContent="space-between">
          <Box>
            <Text fontWeight="600" fontSize="14px" mb="12px">
              We accept
            </Text>
            <UnorderedList fontSize="15px" lineHeight="28px">
              <ListItem>Bank statements</ListItem>
              <ListItem>Tax returns</ListItem>
              <ListItem>Schedule real estate</ListItem>
            </UnorderedList>
          </Box>
          <Box>
            <Text fontWeight="600" fontSize="14px" mb="12px">
              We don't accept
            </Text>
            <UnorderedList fontSize="15px" lineHeight="28px">
              <ListItem>Screenshots</ListItem>
              <ListItem>Mobile phone bills</ListItem>
              <ListItem>Medical bills</ListItem>
              <ListItem>Receipts for purchases</ListItem>
              <ListItem>Insurance statements</ListItem>
            </UnorderedList>
          </Box>
        </HStack>
        {files.length > 0 && (
          <Text fontWeight="500" fontSize="14px">
            Documents
          </Text>
        )}
        <DocumentsUpload files={files} setFiles={setFiles} />
        <Flex justifyContent="space-between">
          <Button onClick={handleBack} size="sm" variant="outline">
            Back
          </Button>
          <Button 
            size="sm" 
            onClick={uploadDocuments} 
            isLoading={isLoading}
            colorScheme="blue"
            isDisabled={files.length === 0}
          >
            Continue
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};
export default FinancialDocuments;
